import { useMemo, useState } from "react";

// components
import { Header } from "components/atoms/Typography";
import {
    BaselineAreaChart,
    EndUseStackedBarChart,
    EndUseSankey,
    FutureProjectionEnergyConsumptionCard,
    FutureProjectionCarbonEmissionsCard,
    AnnualCumulativePicker,
} from "components/organisms/Charts";
import { RadioButtonSelectMode } from "components/organisms/Charts/AnnualCumulativePicker";
import Card from "components/molecules/Card";

// hooks
import { useCurrentAnnualCarbonEmissionsCard } from "queries/BuildingReport/Baseline/useCurrentAnnualCarbonEmissionsCard";
import { useCurrentAnnualEnergyConsumptionCard } from "queries/BuildingReport/Baseline/useCurrentAnnualEnergyConsumptionCard";
import { useCurrentAnnualEndUseChart } from "queries/BuildingReport/Baseline/useCurrentAnnualEndUseChart";
import { buildSankeyData } from "components/organisms/Charts/EndUseSankey";
import { useBuildingCumulativeCarbonEmissionsCard } from "queries/BuildingReport/Baseline/useBuildingCumulativeCarbonEmissionsCard";
import { useCumulativeEnergyConsumptionCard } from "queries/BuildingReport/Baseline/useCumulativeEnergyConsumptionCard";
import { BuildingCumulativeOperatingCostCard } from "components/organisms/Charts/Recommendations/CumulativeOperatingCostCard";
import { useAnnualCarbonEmissionsChart } from "queries/BuildingReport/Baseline/useAnnualCarbonEmissionsChart";
import { useAnnualCarbonEmissionsNaturalGasChart } from "queries/BuildingReport/Baseline/useAnnualCarbonEmissionsNaturalGasChart";
import { useAnnualCarbonEmissionsElectricityChart } from "queries/BuildingReport/Baseline/useAnnualCarbonEmissionsElectricityChart";
import { useAnnualCarbonEmissionsCard } from "queries/BuildingReport/Baseline/useAnnualCarbonEmissionsCard";
import { useAnnualEnergyConsumptionCard } from "queries/BuildingReport/Baseline/useAnnualEnergyConsumptionCard";

// constants
import { CURRENT_YEAR } from "utils/constants";

// styling
import "./BaselineReport.css";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { useSelectedBuildingModelCarbonReductionPlan } from "hooks/Building/useBuildingModelCarbonReductionPlan";

function BuildingPageBaselineReport() {
    const {
        buildingModel,
        bmcrp: selectedBMCRP,
        isLoading: isLoadingBMCRP,
    } = useSelectedBuildingModelCarbonReductionPlan();

    if (isLoadingBMCRP || !buildingModel) {
        return <LoadingSpinner />;
    }

    return (
        <div className="baseline-building-report">
            <Header size="medium">Your building at a glance - {CURRENT_YEAR}</Header>
            <div className="cards-and-chart-grid">
                <div className="cards">
                    <CurrentAnnualCarbonEmissionsCard />
                    <CurrentAnnualEnergyConsumptionCard />
                    <BuildingCumulativeOperatingCostCard
                        buildingModel={buildingModel!}
                        selectedBMCRP={selectedBMCRP}
                        isBaseline={true}
                        year={CURRENT_YEAR}
                    />
                </div>
                <CurrentAnnualEndUseChart />
            </div>
            <Header size="medium">Future projection</Header>
            <div className="cards-and-chart-grid">
                <FutureProjectionCards />
                <AnnualCarbonEmissionsChart />
            </div>
            <div className="side-by-side-charts-grid">
                <AnnualCarbonEmissionsNaturalGasChart />
                <AnnualCarbonEmissionsElectricityChart />
            </div>
        </div>
    );
}

function CurrentAnnualCarbonEmissionsCard() {
    const { data, isLoading, error } = useCurrentAnnualCarbonEmissionsCard();

    const cardData = useMemo(() => {
        if (!data) return undefined;
        const { totalCarbonEmissions, totalEmissionIntensity } = data;

        return {
            header: {
                value: totalCarbonEmissions,
                displayInfoKey: "carbonEmissions",
            },
            sections: [
                [
                    {
                        value: totalEmissionIntensity,
                        displayInfoKey: "carbonEmissionIntensity",
                    },
                ],
            ],
        };
    }, [data]);

    return (
        <Card
            data={cardData}
            loading={isLoading || !cardData}
            error={error?.toString()}
            cardTitle="Current Annual Carbon Emissions"
        />
    );
}

function CurrentAnnualEnergyConsumptionCard() {
    const { data, isLoading, error } = useCurrentAnnualEnergyConsumptionCard();

    const cardData = useMemo(() => {
        if (!data) return undefined;
        const { totalEnergyConsumption, totalEnergyIntensity } = data;

        return {
            header: {
                value: totalEnergyConsumption,
                displayInfoKey: "annualEnergyConsumptionCurrent",
            },
            sections: [
                [
                    {
                        value: totalEnergyIntensity,
                        displayInfoKey: "annualEnergyUseIntensityCurrent",
                    },
                ],
            ],
        };
    }, [data]);

    return (
        <Card
            data={cardData}
            loading={isLoading || !data}
            error={error?.toString()}
            cardTitle="Current Annual Energy Consumption"
        />
    );
}

function CurrentAnnualEndUseChart() {
    const { data, isLoading, error } = useCurrentAnnualEndUseChart();

    const sankeyData = useMemo(() => {
        if (!data) return null;
        const { carbonEmissions, energyConsumption, utilityCost } = data;
        return {
            carbonEmissions: buildSankeyData(carbonEmissions),
            energyConsumption: buildSankeyData(energyConsumption),
            utilityCost: buildSankeyData(utilityCost),
        };
    }, [data]);

    return <EndUseSankey loading={isLoading || !sankeyData} data={sankeyData} error={error?.toString()} />;
}

function FutureProjectionCards() {
    const [selected, setSelected] = useState<RadioButtonSelectMode>("cumulative");
    const [sliderCurrentYear, setSliderCurrentYear] = useState(CURRENT_YEAR);
    const { bmcrp: selectedBMCRP, buildingModel } = useSelectedBuildingModelCarbonReductionPlan();

    const onChangeSelect = () => {
        setSelected((prev) => {
            if (prev === "cumulative") return "annual";
            return "cumulative";
        });
    };

    return (
        <div>
            <AnnualCumulativePicker
                selected={selected}
                onChangeSelect={onChangeSelect}
                currentSliderYear={sliderCurrentYear}
                setCurrentSliderYear={setSliderCurrentYear}
            />
            <div className="cards">
                {selected === "cumulative" ? (
                    <>
                        <CumulativeCarbonEmissionsCard />
                        <CumulativeEnergyConsumptionCard />
                        <BuildingCumulativeOperatingCostCard
                            buildingModel={buildingModel!}
                            selectedBMCRP={selectedBMCRP}
                            isBaseline={true}
                            year={null}
                        />
                    </>
                ) : (
                    <>
                        <AnnualCarbonEmissionsCard year={sliderCurrentYear} />
                        <AnnualEnergyConsumptionCard year={sliderCurrentYear} />
                        <BuildingCumulativeOperatingCostCard
                            buildingModel={buildingModel!}
                            selectedBMCRP={selectedBMCRP}
                            isBaseline={true}
                            year={sliderCurrentYear}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

function CumulativeCarbonEmissionsCard() {
    const { data, isLoading, error } = useBuildingCumulativeCarbonEmissionsCard();

    return (
        <FutureProjectionCarbonEmissionsCard
            loading={isLoading || !data}
            cumulative={true}
            data={data}
            error={error?.toString()}
        />
    );
}

function CumulativeEnergyConsumptionCard() {
    const { data, isLoading, error } = useCumulativeEnergyConsumptionCard();

    return (
        <FutureProjectionEnergyConsumptionCard
            loading={isLoading || !data}
            cumulative={true}
            data={data}
            error={error?.toString()}
        />
    );
}

function AnnualCarbonEmissionsCard({ year }: { year: number }) {
    const { data, isLoading, error } = useAnnualCarbonEmissionsCard();

    const cardData = useMemo(() => {
        if (!data) return undefined;
        return data.find((datum) => year === datum.year);
    }, [data, year]);

    return (
        <FutureProjectionCarbonEmissionsCard
            loading={isLoading || !cardData}
            cumulative={false}
            data={cardData}
            error={error?.toString()}
        />
    );
}

function AnnualEnergyConsumptionCard({ year }: { year: number }) {
    const { data, isLoading, error } = useAnnualEnergyConsumptionCard();

    const cardData = useMemo(() => {
        if (!data) return undefined;
        return data.find((datum) => year === datum.year);
    }, [data, year]);

    return (
        <FutureProjectionEnergyConsumptionCard
            loading={isLoading || !data}
            cumulative={false}
            data={cardData}
            error={error?.toString()}
        />
    );
}

function AnnualCarbonEmissionsChart() {
    const { data, isLoading, error } = useAnnualCarbonEmissionsChart();

    const areaChartData = useMemo(
        () =>
            data?.map((datum) => ({
                areaY: datum?.totalCarbonEmissions || 0,
                x: datum?.year || 0,
            })),
        [data]
    );

    return <BaselineAreaChart data={areaChartData} loading={isLoading || !areaChartData} error={error?.toString()} />;
}

function AnnualCarbonEmissionsNaturalGasChart() {
    const { data, isLoading, error } = useAnnualCarbonEmissionsNaturalGasChart();

    return (
        <EndUseStackedBarChart
            data={data as any}
            loading={isLoading || !data}
            fuelTypeKey="naturalGas"
            error={error?.toString()}
        />
    );
}

function AnnualCarbonEmissionsElectricityChart() {
    const { data, isLoading, error } = useAnnualCarbonEmissionsElectricityChart();

    return (
        <EndUseStackedBarChart
            data={data}
            loading={isLoading || !data}
            fuelTypeKey="electricity"
            error={error?.toString()}
        />
    );
}

export default BuildingPageBaselineReport;
